import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Check, Save, ChevronDown, Moon, Activity, Heart, Brain, BookOpen, ArrowLeft } from "lucide-react";
import BigSlider from "@/components/BigSlider";
import { formatDateLong, parseISODate, todayLocalISO, toISO } from "@/lib/calendar";

const ICONS = { sleep: Moon, physical: Activity, mood: Heart, thoughts: Brain, learning: BookOpen };

function isWithinEditWindow(iso, today) {
  if (!iso) return true;
  const target = parseISODate(iso);
  const todayD = parseISODate(today);
  const diff = Math.round((todayD - target) / 86400000);
  return diff >= 0 && diff <= 30;
}

export default function DailyLog() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const focus = params.get("focus");
  const requestedDate = params.get("date");
  const todayISO = todayLocalISO();
  const effectiveDate = requestedDate || todayISO;
  const isHistorical = !!requestedDate && requestedDate !== todayISO;

  const [dimensions, setDimensions] = useState([]);
  const [data, setData] = useState({});
  const [completed, setCompleted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [openSection, setOpenSection] = useState(null);
  const lastSerialized = useRef("");
  const blockedRef = useRef(false);

  useEffect(() => {
    // Guard: historical day outside the 30-day edit window
    if (isHistorical && !isWithinEditWindow(requestedDate, todayISO)) {
      blockedRef.current = true;
      toast.error("Los registros pueden editarse hasta 30 días hacia atrás.");
      const t = setTimeout(() => navigate("/calendario", { replace: true }), 100);
      return () => clearTimeout(t);
    }
    let alive = true;
    // Always load by local date — never trust the server's UTC notion of "today"
    const logEndpoint = `/logs/by-date/${effectiveDate}`;
    Promise.all([api.get("/dimensions"), api.get(logEndpoint)]).then(([d, t]) => {
      if (!alive) return;
      setDimensions(d.data.dimensions);
      const log = t.data?.log;
      if (log) {
        setData(log.data || {});
        setCompleted(!!log.completed);
        lastSerialized.current = JSON.stringify(log.data || {});
      }
      const first = focus && d.data.dimensions.find((x) => x.id === focus);
      setOpenSection(first ? focus : d.data.dimensions[0]?.id);
    }).finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [focus, requestedDate, isHistorical, effectiveDate, todayISO, navigate]);

  // Autosave with debounce
  useEffect(() => {
    if (loading || blockedRef.current) return;
    const serialized = JSON.stringify(data);
    if (serialized === lastSerialized.current) return;
    const t = setTimeout(async () => {
      try {
        setSaving(true);
        await api.post("/logs/today", {
          data,
          completed: false,
          // Always include the date being edited + the client's local today,
          // so the server doesn't mistake the user's local day for its UTC day.
          date: effectiveDate,
          client_today: todayISO,
        });
        lastSerialized.current = serialized;
      } catch (e) {
        // silent fail; user can retry on complete
      } finally {
        setSaving(false);
      }
    }, 800);
    return () => clearTimeout(t);
  }, [data, loading, isHistorical, effectiveDate, todayISO]);

  const progress = useMemo(() => {
    if (!dimensions.length) return 0;
    let filled = 0;
    dimensions.forEach((dim) => {
      const dd = data[dim.id];
      if (dd && Object.values(dd).some((v) => v != null && v !== "" && (!Array.isArray(v) || v.length))) {
        filled += 1;
      }
    });
    return Math.round((filled / dimensions.length) * 100);
  }, [data, dimensions]);

  const setField = (dimId, fieldId, value) => {
    setData((prev) => ({ ...prev, [dimId]: { ...(prev[dimId] || {}), [fieldId]: value } }));
  };

  const submitComplete = async () => {
    setSaving(true);
    try {
      await api.post("/logs/today", {
        data,
        completed: true,
        date: effectiveDate,
        client_today: todayISO,
      });
      const wasCompleted = completed;
      setCompleted(true);
      toast.success(
        isHistorical
          ? "Registro histórico actualizado"
          : wasCompleted
            ? "Cambios guardados"
            : "¡Registro de hoy completado!"
      );
      if (isHistorical) {
        setTimeout(() => navigate("/calendario"), 600);
      } else if (!wasCompleted) {
        setTimeout(() => navigate("/dashboard"), 600);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="py-16 text-center text-ink-600" data-testid="log-loading">Cargando…</div>;
  }

  const headline = isHistorical
    ? `Editando ${formatDateLong(effectiveDate)}`
    : completed
      ? "Ajustá los detalles de hoy"
      : "¿Cómo estuvo tu día?";

  const subline = isHistorical
    ? "Estás editando un registro pasado. Los cambios actualizan tu historial automáticamente."
    : "Podés modificar tu registro hasta el final del día.";

  return (
    <div className="space-y-6 animate-fade-up max-w-xl mx-auto" data-testid="daily-log-page" data-historical={isHistorical ? "true" : "false"}>
      {isHistorical && (
        <button
          type="button"
          onClick={() => navigate("/calendario")}
          className="inline-flex items-center gap-1.5 font-dmsans text-xs text-ink-600 hover:text-ink-900 transition"
          data-testid="log-back-to-calendar"
        >
          <ArrowLeft size={14} />
          Volver al calendario
        </button>
      )}
      <header className="space-y-3">
        <div className="flex items-end justify-between gap-3">
          <div>
            <p className="tk-label">
              {isHistorical ? "Edición histórica" : completed ? "Editar registro" : "Registro diario"}
            </p>
            <h1 className="font-outfit text-3xl text-ink-900 mt-1 capitalize">{headline}</h1>
            <p className="font-dmsans text-xs text-ink-600 mt-1">{subline}</p>
          </div>
          <div className="text-right">
            <p className="tk-label">Progreso</p>
            <p className="font-outfit text-2xl text-sage-600" data-testid="log-progress-pct">{progress}%</p>
          </div>
        </div>
        <div className="h-2 w-full rounded-full bg-sand-100 overflow-hidden" data-testid="log-progress-bar">
          <div
            className="h-full bg-gradient-to-r from-sage-400 to-sage-500 transition-all duration-500"
            style={{ width: `${progress}%` }}
          />
        </div>
        <div className="flex items-center gap-2 text-xs text-ink-600">
          {saving ? <Save size={14} className="animate-pulse" /> : <Check size={14} className="text-sage-500" />}
          <span data-testid="log-save-status">{saving ? "Guardando…" : "Guardado automáticamente"}</span>
        </div>
      </header>

      <div className="space-y-3">
        {dimensions.map((dim) => {
          const Icon = ICONS[dim.id] || Moon;
          const isOpen = openSection === dim.id;
          const dimData = data[dim.id] || {};
          const filled = Object.values(dimData).some((v) => v != null && v !== "" && (!Array.isArray(v) || v.length));
          return (
            <section
              key={dim.id}
              className={`tk-glass rounded-3xl overflow-hidden transition-all ${isOpen ? "shadow-[0_18px_50px_-28px_rgba(83,109,83,0.22)]" : ""}`}
              data-testid={`log-section-${dim.id}`}
            >
              <button
                onClick={() => setOpenSection(isOpen ? null : dim.id)}
                className="w-full flex items-center justify-between gap-3 p-5 text-left"
                data-testid={`log-section-toggle-${dim.id}`}
              >
                <div className="flex items-center gap-3">
                  <div
                    className="h-11 w-11 rounded-2xl flex items-center justify-center"
                    style={{ background: `${dim.color}22`, color: dim.color }}
                  >
                    <Icon size={20} />
                  </div>
                  <div>
                    <p className="font-outfit text-lg text-ink-900">{dim.name}</p>
                    <p className="font-dmsans text-xs text-ink-600">
                      {filled ? "Completado" : "Sin completar"}
                    </p>
                  </div>
                </div>
                <ChevronDown
                  size={20}
                  className={`text-ink-600 transition-transform ${isOpen ? "rotate-180" : ""}`}
                />
              </button>
              {isOpen && (
                <div className="px-5 pb-6 pt-2 space-y-5 border-t border-cream-100/60">
                  {dim.fields.map((field) => (
                    <FieldRenderer
                      key={field.id}
                      dimId={dim.id}
                      field={field}
                      value={dimData[field.id]}
                      sectionValues={dimData}
                      onChange={(v) => setField(dim.id, field.id, v)}
                      setField={setField}
                    />
                  ))}
                </div>
              )}
            </section>
          );
        })}
      </div>

      <div className="sticky bottom-20 md:bottom-4 left-0 right-0 pt-4">
        <div className="tk-card flex flex-col sm:flex-row gap-3 items-center justify-between">
          <p className="font-dmsans text-sm text-ink-600">
            {isHistorical
              ? "Recalculamos automáticamente los scores, insights y correlaciones al guardar."
              : completed
                ? "Registro completado. Podés seguir editando hasta el final del día."
                : "Cuando termines, marcalo como completado."}
          </p>
          <button
            onClick={submitComplete}
            disabled={saving}
            className={completed || isHistorical ? "tk-btn-primary w-full sm:w-auto" : "tk-btn-primary w-full sm:w-auto"}
            data-testid="log-complete-btn"
          >
            {isHistorical ? <>Guardar cambios <Check size={16} /></> : completed ? <>Guardar cambios <Check size={16} /></> : "Marcar como completo"}
          </button>
        </div>
      </div>
    </div>
  );
}

function FieldRenderer({ dimId, field, value, sectionValues, onChange, setField }) {
  if (field.depends_on) {
    const dep = sectionValues[field.depends_on];
    if (!dep) return null;
  }
  // Sprint 7 V9: intensity (and any future "session-only" field) appears
  // only when the canonical session predicate holds — minutes > 0.
  if (field.depends_on_session) {
    const m = sectionValues.minutes;
    if (!(typeof m === "number" && m > 0)) return null;
  }

  if (field.type === "number") {
    if (field.control === "slider") {
      const isPhysicalMinutes = dimId === "physical" && field.id === "minutes";
      return (
        <div className="space-y-3">
          <BigSlider
            label={field.label}
            value={value}
            onChange={onChange}
            min={field.min ?? 0}
            max={field.max ?? 100}
            step={field.step ?? 1}
            unit={field.unit || ""}
            display={field.display || "plain"}
            ticks={field.ticks}
            testId={`field-${dimId}-${field.id}`}
          />
          {isPhysicalMinutes && (
            <RestDayCTA
              isActive={value === 0}
              onTap={() => {
                onChange(0);                              // canonical REST
                if (setField) setField(dimId, "intensity", null); // I5: purge
              }}
            />
          )}
        </div>
      );
    }
    return (
      <div>
        <label className="tk-label mb-1.5 block">{field.label}</label>
        <input
          type="number"
          inputMode="decimal"
          step={field.step || 1}
          min={field.min}
          max={field.max}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
          className="tk-input"
          placeholder={field.unit ? `Ej. 8 ${field.unit}` : ""}
          data-testid={`field-${dimId}-${field.id}`}
        />
      </div>
    );
  }
  if (field.type === "time") {
    return (
      <div>
        <label className="tk-label mb-1.5 block">{field.label}</label>
        <input
          type="time"
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value || null)}
          className="tk-input"
          data-testid={`field-${dimId}-${field.id}`}
        />
      </div>
    );
  }
  if (field.type === "text") {
    return (
      <div>
        <label className="tk-label mb-1.5 block">{field.label}</label>
        <textarea
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          rows={3}
          className="tk-input resize-none"
          placeholder={field.placeholder || ""}
          data-testid={`field-${dimId}-${field.id}`}
        />
      </div>
    );
  }
  if (field.type === "boolean") {
    return (
      <div>
        <label className="tk-label mb-1.5 block">{field.label}</label>
        <div className="grid grid-cols-2 gap-2" data-testid={`field-${dimId}-${field.id}`}>
          {[
            { id: true, label: "Sí" },
            { id: false, label: "No" },
          ].map((opt) => (
            <button
              key={String(opt.id)}
              type="button"
              onClick={() => onChange(opt.id)}
              className={`rounded-2xl border px-4 py-3 font-dmsans text-sm transition ${
                value === opt.id
                  ? "border-sage-500 bg-sage-50 text-sage-700"
                  : "border-sand-200 bg-white text-ink-900 hover:border-sage-500/40"
              }`}
              data-testid={`field-${dimId}-${field.id}-${opt.id ? "yes" : "no"}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>
    );
  }
  if (field.type === "scale") {
    const val = value ?? Math.round((field.min + field.max) / 2);
    return (
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="tk-label">{field.label}</label>
          <span className="font-outfit text-base text-sage-600" data-testid={`field-${dimId}-${field.id}-value`}>{val}</span>
        </div>
        <input
          type="range"
          min={field.min}
          max={field.max}
          step="1"
          value={val}
          onChange={(e) => onChange(Number(e.target.value))}
          className="tk-range w-full"
          data-testid={`field-${dimId}-${field.id}`}
        />
        {(field.left_label || field.right_label) && (
          <div className="flex justify-between mt-1.5 text-[11px] text-ink-600 font-dmsans">
            <span>{field.left_label}</span>
            <span>{field.right_label}</span>
          </div>
        )}
      </div>
    );
  }
  if (field.type === "multi_select") {
    const selected = Array.isArray(value) ? value : [];
    const toggle = (optId) => {
      if (selected.includes(optId)) onChange(selected.filter((x) => x !== optId));
      else onChange([...selected, optId]);
    };
    return (
      <div>
        <label className="tk-label mb-2 block">{field.label}</label>
        <div className="flex flex-wrap gap-2" data-testid={`field-${dimId}-${field.id}`}>
          {field.options.map((opt) => {
            const isSel = selected.includes(opt.id);
            return (
              <button
                key={opt.id}
                type="button"
                onClick={() => toggle(opt.id)}
                className={`tk-chip ${isSel ? "tk-chip-selected" : ""}`}
                data-testid={`field-${dimId}-${field.id}-${opt.id}`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>
    );
  }
  if (field.type === "tri_state") {
    const sel = value;
    return (
      <div>
        <label className="tk-label mb-2 block">{field.label}</label>
        <div className="grid grid-cols-3 gap-2" data-testid={`field-${dimId}-${field.id}`}>
          {field.options.map((opt) => (
            <button
              key={opt.id}
              type="button"
              onClick={() => onChange(opt.id)}
              className={`rounded-2xl border px-3 py-2.5 font-dmsans text-sm transition ${
                sel === opt.id ? "border-sage-500 bg-sage-50 text-sage-700" : "border-sand-200 bg-white text-ink-900 hover:border-sage-500/40"
              }`}
              data-testid={`field-${dimId}-${field.id}-${opt.id}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>
    );
  }
  return null;
}

/**
 * RestDayCTA — pill button under the physical-minutes slider that explicitly
 * commits `minutes = 0` (canonical REST state per Sprint 7 V9).
 *
 * Distinct from dragging the slider to 0 only in affordance: tapping the pill
 * gives immediate "✓ Día de descanso registrado" feedback. Both paths persist
 * the same `{minutes: 0}` document — the SSOT does not care how the user
 * arrived at the rest state, only that they chose it.
 */
function RestDayCTA({ isActive, onTap }) {
  return (
    <button
      type="button"
      onClick={onTap}
      data-testid="physical-rest-day-cta"
      aria-pressed={isActive}
      className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-dmsans transition-all ${
        isActive
          ? "border-sage-500 bg-sage-50 text-sage-700"
          : "border-sand-200 bg-white/70 text-ink-700 hover:border-sage-400/50 hover:bg-cream-50"
      }`}
    >
      <span aria-hidden="true">🌙</span>
      <span>{isActive ? "Día de descanso registrado" : "Día de descanso"}</span>
    </button>
  );
}
